import { LoadingPlace } from '../models/booking';
import { Negotiation } from '../models/negotiation';
import { ContactPerson, Customer, Geography, OfferScoreComponents } from '../models/offer';
import { LoadSecurity, SwappableBodyType, TrailerEquipment, TrailerProperty, TrailerType, VehicleType } from '../models/vehicle';

export type MarketplaceName = string;

/** Timocom-specific fields stored in offer.pluginData. Must be kept in sync with TimocomPluginData on the server. */
export interface TimocomPluginData {
  useMessenger?: boolean;
  acceptQuotes?: boolean;
  logisticsDocumentTypes?: string[];
}

/**
 * Static scores that only depend on the offer itself (not vehicle/booking context)
 * These are a subset of OfferScoreComponents, computed once at offer creation and stored in database.
 * Static scores are subset of OfferScoringWeights: route_distance, destination_score, risk_assessment,
 * tonnage_efficiency, and payment_terms. These 5 components don't require vehicle/booking context.
 */
export type OfferStaticScores = Pick<OfferScoreComponents, 'route_distance' | 'destination_score' | 'risk_assessment' | 'tonnage_efficiency' | 'payment_terms' | 'computed_at'>;

/**
 * Base Offer interface from API
 */
export interface Offer {
  id: number;
  createdAt: string;
  updatedOn: string;
  contactPerson?: ContactPerson;
  creationDateTime?: string;
  deeplink?: string;
  publicRemark?: string;
  vehicleProperties: VehicleProperties;
  additionalInformation?: string[];
  freightDescription?: string;
  lengthM?: number;
  weightT?: number;
  loadingPlaces: LoadingPlace[];
  paymentDueWithinDays?: number;
  fePrice?: number;
  distanceKms: number;
  computedKms?: number;
  estimatedPrice?: number;
  acceptedPrice?: number;
  status: OfferStatus;
  negotiationStep?: number;
  externalId: string;
  externalPlatform: MarketplaceName;
  tenant_id: number;
  startPlace: Geography;
  endPlace: Geography;
  earliestStart: Date;
  latestStart: Date;
  earliestEnd: Date;
  latestEnd: Date;
  lastExistingAt: Date;
  staticScores?: OfferStaticScores;
  pluginData?: Record<string, unknown>;
}

/** Type-safe accessor for Timocom-specific pluginData. Returns undefined for non-Timocom offers. */
export const timocomPluginData = (offer: { externalPlatform?: string; pluginData?: Record<string, unknown> }): TimocomPluginData | undefined => offer.externalPlatform === 'TimocomMarketplace' ? offer.pluginData as TimocomPluginData : undefined;

/**
 * PotentialOffer extends Offer with vehicle-specific computed data
 */
export interface PotentialOffer extends Offer {
  negotiations?: Negotiation[];
  customer?: Customer;
  workedAlready: boolean;
  arrivalEstimate?: string;
  score?: number;
  // Pre-computed scores from vehicle_offer_booking_match table
  clientScore?: number;
  pickupDistanceScore?: number;
  planningScore?: number;
  finalScore?: number;
  // Pre-computed price data from vehicle_offer_booking_match table.
  // approachPrice/Distance/Tolls are null when no approach source was available at compute time
  // (no vehicle currentPosition and no geocoded end on the prior booking) — distinct from 0 km.
  offerDistance?: number;
  offerTolls?: boolean;
  approachPrice?: number | null;
  approachDistance?: number | null;
  approachTolls?: boolean | null;
  totalPrice?: number;
  totalDistance?: number;
  offerPreciseMode?: boolean;
  approachPreciseMode?: boolean;
  // Computed scores object
  computedScores?: OfferScoreComponents;
  // Computed prices object
  computedPrices?: {
    estimatedPrice?: number;
    offerDistance?: number;
    offerTolls?: boolean;
    approachPrice?: number | null;
    approachDistance?: number | null;
    approachTolls?: boolean | null;
    totalPrice?: number;
    totalDistance?: number;
    offerPreciseMode?: boolean;
    approachPreciseMode?: boolean;
  };
  // UI State Properties
  negotiationPrice?: number | null;
  negotiation_id?: number | null;
  isConfirming?: boolean;
  isCanceling?: boolean;
  isClosing?: boolean;
  // Legacy properties for backward compatibility
  cnt?: string;
  emailRefs?: any[];
  initiallyOfferedPrice?: number | null;
  lastOfferedPrice?: number | null;
  // Other vehicle currently negotiating this offer (if any)
  otherNegotiatingVehicle?: {
    vehicleId: number;
    truckPlate: string;
  };
  unreadEmails?: number;
}

/**
 * Vehicle properties for offers
 */
export interface VehicleProperties {
  body: TrailerType[];
  bodyProperty: TrailerProperty[];
  equipment: TrailerEquipment[];
  loadSecuring: LoadSecurity[];
  swapBody: SwappableBodyType[]
  type: VehicleType[];
}

/**
 * Props for the VehicleOffersTable component
 */
export interface VehicleOffersTableProps {
  vehicleId: string;
  visible: boolean;
  onTruckClick?: (truckId: string) => void;
}

/**
 * Status information with color coding
 */
export interface StatusInfo {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

/**
 * Offer status enum
 */
export enum OfferStatus {
  WITHOUT_CONTACT = 0,
  WITH_CONTACT = 1,
  NEGOTIATION_STARTED = 2,
  NEGOTIATION_PRICE_RECEIVED = 3,
  NEGOTIATION_PRICE_OFFERED = 4,
  NEGOTIATION_ACCEPTED = 5, // We accepted but not yet received order
  NEGOTIATION_DECLINED = 6,
  NEGOTIATION_NEED_HUMAN = 7,
  NEGOTIATION_WAITING_FINAL_CONFIRM = 8, // If we need our manager to confirm before accepting
  NEGOTIATION_MANUALLY_CLOSED = 9,
  NEGOTIATION_BACK_CONFIRMED = 10,
  NEGOTIATION_MANUALLY_CLOSED_UNDISCLOSED // We accepted and received order
}
